(() => {
  const existing = window.ACMHome;
  if (existing && typeof existing.boot === "function") {
    existing.boot(document);
    return;
  }

  const selectAll = (scope, selector) => {
    const matches = scope.matches && scope.matches(selector) ? [scope] : [];
    return matches.concat(Array.from(scope.querySelectorAll(selector)));
  };

  const restartAnimation = (element, className) => {
    if (!element) return;
    element.classList.remove(className);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => element.classList.add(className));
    });
  };

  const initReveals = (scope) => {
    const elements = selectAll(scope, "[data-acm-reveal]").filter(
      (element) => !element.dataset.acmRevealBound,
    );
    if (!elements.length) return;

    elements.forEach((element) => {
      element.dataset.acmRevealBound = "true";
    });

    if (!("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("is-revealed"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" },
    );

    elements.forEach((element) => observer.observe(element));
  };

  const initCustomizers = (scope) => {
    selectAll(scope, "[data-acm-customizer]").forEach((root) => {
      if (root.dataset.acmCustomizerBound) return;
      root.dataset.acmCustomizerBound = "true";

      const image = root.querySelector("[data-acm-product-image]");
      const selectedMat = root.querySelector("[data-acm-selected-mat]");
      const selectedTone = root.querySelector("[data-acm-selected-tone]");
      const matLabel = root.querySelector("[data-acm-mat-label]");
      const edgeLabel = root.querySelector("[data-acm-edge-label]");
      const summaryMat = root.querySelector("[data-acm-summary-mat]");
      const summaryEdge = root.querySelector("[data-acm-summary-edge]");
      const matButtons = Array.from(root.querySelectorAll("[data-acm-mat]"));
      const edgeButtons = Array.from(root.querySelectorAll("[data-acm-edge]"));

      const setMat = (name) => {
        const source = matButtons.find((button) => button.dataset.acmMat === name);
        if (!source) return;

        matButtons.forEach((button) => {
          const active = button.dataset.acmMat === name;
          button.classList.toggle("is-selected", active);
          button.setAttribute("aria-pressed", String(active));
        });

        if (image && source.dataset.acmImage && image.src !== source.dataset.acmImage) {
          image.src = source.dataset.acmImage;
          image.alt = `${name} tailored EVA car mat set with ${edgeLabel?.textContent || "Black"} edge`;
          restartAnimation(image, "acm-is-changing");
        }

        if (selectedMat) selectedMat.textContent = name;
        if (selectedTone) selectedTone.textContent = source.dataset.acmTone || "";
        if (matLabel) matLabel.textContent = name;
        if (summaryMat) summaryMat.textContent = name;
      };

      const setEdge = (name) => {
        const source = edgeButtons.find((button) => button.dataset.acmEdge === name);
        if (!source) return;

        edgeButtons.forEach((button) => {
          const active = button.dataset.acmEdge === name;
          button.classList.toggle("is-selected", active);
          button.setAttribute("aria-pressed", String(active));
        });

        root.style.setProperty("--acm-selected-edge", source.dataset.acmColour || "#111111");
        if (edgeLabel) edgeLabel.textContent = name;
        if (summaryEdge) summaryEdge.textContent = name;
        if (image) {
          const currentMat = summaryMat?.textContent || "Black";
          image.alt = `${currentMat} tailored EVA car mat set with ${name} edge`;
        }
      };

      matButtons.forEach((button) => {
        button.addEventListener("click", () => setMat(button.dataset.acmMat));
      });

      edgeButtons.forEach((button) => {
        button.addEventListener("click", () => setEdge(button.dataset.acmEdge));
      });
    });
  };

  const initProtectionStories = (scope) => {
    selectAll(scope, "[data-acm-protection]").forEach((root) => {
      if (root.dataset.acmProtectionBound) return;
      root.dataset.acmProtectionBound = "true";

      const orbit = root.querySelector(".acm-protection__orbit");
      const popover = root.querySelector(".acm-feature-popover");
      const popoverIcon = root.querySelector("[data-acm-popover-icon]");
      const popoverIndex = root.querySelector("[data-acm-popover-index]");
      const popoverTitle = root.querySelector("[data-acm-popover-title]");
      const popoverDetail = root.querySelector("[data-acm-popover-detail]");
      const featureNumber = root.querySelector("[data-acm-feature-number]");
      const steps = Array.from(root.querySelectorAll("[data-acm-protection-step]"));

      const setActive = (index) => {
        const activeStep = steps[index];
        if (!activeStep || !orbit || !popover) return;

        steps.forEach((step, stepIndex) => {
          step.classList.toggle("is-active", stepIndex === index);
        });

        for (let state = 0; state < 6; state += 1) {
          orbit.classList.remove(`feature-${state}`);
          popover.classList.remove(`popover-${state}`);
        }
        orbit.classList.add(`feature-${index}`);
        popover.classList.add(`popover-${index}`);

        const number = String(index + 1).padStart(2, "0");
        if (featureNumber) featureNumber.textContent = number;
        if (popoverIndex) popoverIndex.textContent = `IN FOCUS · ${number}`;
        if (popoverTitle) popoverTitle.textContent = activeStep.dataset.title || "";
        if (popoverDetail) popoverDetail.textContent = activeStep.dataset.detail || "";

        const stepIcon = activeStep.querySelector(".acm-feature-icon");
        if (popoverIcon && stepIcon) popoverIcon.innerHTML = stepIcon.innerHTML;
        restartAnimation(popover, "acm-is-changing");
      };

      if (!("IntersectionObserver" in window)) {
        setActive(0);
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(Number(entry.target.dataset.index));
          });
        },
        { threshold: 0.62, rootMargin: "-15% 0px -20% 0px" },
      );

      steps.forEach((step) => observer.observe(step));
      root._acmCleanup = () => observer.disconnect();
    });
  };

  const initOwnerProof = (scope) => {
    selectAll(scope, "[data-acm-owner-proof]").forEach((root) => {
      if (root.dataset.acmOwnerBound) return;
      root.dataset.acmOwnerBound = "true";

      const slides = Array.from(root.querySelectorAll("[data-acm-owner-slide]"));
      const dots = Array.from(root.querySelectorAll("[data-acm-owner-dot]"));
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      let activeIndex = 0;
      let timer;

      const show = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          slide.hidden = !active;
          slide.classList.toggle("is-active", active);
        });
        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === activeIndex;
          dot.classList.toggle("is-active", active);
          dot.setAttribute("aria-pressed", String(active));
        });
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
      };

      const start = () => {
        stop();
        if (!reducedMotion && slides.length > 1) {
          timer = window.setInterval(() => show(activeIndex + 1), 6500);
        }
      };

      dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          show(Number(dot.dataset.index));
          start();
        });
      });

      root.addEventListener("mouseenter", stop);
      root.addEventListener("mouseleave", start);
      root.addEventListener("focusin", stop);
      root.addEventListener("focusout", start);
      show(0);
      start();

      root._acmCleanup = () => stop();
    });
  };

  const boot = (scope = document) => {
    document.documentElement.classList.add("acm-motion-ready");
    initReveals(scope);
    initCustomizers(scope);
    initProtectionStories(scope);
    initOwnerProof(scope);
  };

  window.ACMHome = { boot };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document), { once: true });
  } else {
    boot(document);
  }

  document.addEventListener("shopify:section:load", (event) => boot(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-acm-protection], [data-acm-owner-proof]").forEach((root) => {
      if (typeof root._acmCleanup === "function") root._acmCleanup();
    });
  });
})();
